import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Gift, AlertCircle, Mail, RefreshCw, Lock } from 'lucide-react';
interface Participant {
  name: string;
  email?: string;
  assignedTo: string;
  hasRevealed: boolean;
}
interface Game {
  id: string;
  gameName: string;
  createdAt: number;
  rules: {
    allowedGifts: string;
    budget: number;
    currency: string;
  };
  participants: Participant[];
}
export function SecretSantaGame() {
  const {
    gameId
  } = useParams<{
    gameId: string;
  }>();
  const navigate = useNavigate();
  const [selectedName, setSelectedName] = useState('');
  const [error, setError] = useState('');
  const [game, setGame] = useState<Game | null>(null);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    loadGame();
  }, [gameId]);
  const loadGame = () => {
    setLoading(true);
    setError('');
    try {
      // Check if this browser is locked to a specific participant
      const lockedUser = localStorage.getItem(`secretSanta_locked_${gameId}`);
      if (lockedUser) {
        setIsLocked(true);
        console.log('🔒 Browser locked to user:', lockedUser);
      }
      // Load game from localStorage
      const savedGames = localStorage.getItem('secretSantaGames');
      if (!savedGames) {
        setError('No games found in storage. The game may have been deleted or the link is incorrect.');
        setLoading(false);
        return;
      }
      let games: Game[];
      try {
        games = JSON.parse(savedGames);
      } catch (parseError) {
        console.error('Error parsing games:', parseError);
        setError('Game data is corrupted. Please contact the organizer for a new link.');
        setLoading(false);
        return;
      }
      const foundGame = games.find(g => g.id === gameId);
      if (!foundGame) {
        console.error('Game not found. Available game IDs:', games.map(g => g.id));
        setError('Game not found. Please check the URL or contact the organizer.');
        setLoading(false);
        return;
      }
      setGame(foundGame);
      console.log('✅ Game loaded successfully:', foundGame.gameName);
      // If locked, auto-login the locked user
      if (lockedUser) {
        const lockedParticipant = foundGame.participants.find(p => p.name === lockedUser);
        if (lockedParticipant) {
          setParticipant(lockedParticipant);
          setSelectedName(lockedUser);
        }
      } else {
        // Check for session
        const sessionUser = sessionStorage.getItem(`secretSanta_user_${gameId}`);
        if (sessionUser) {
          try {
            const user: Participant = JSON.parse(sessionUser);
            const stillExists = foundGame.participants.find(p => p.name === user.name);
            if (stillExists) {
              setParticipant(stillExists);
              setSelectedName(user.name);
              console.log('✅ Session restored for:', user.name);
            } else {
              sessionStorage.removeItem(`secretSanta_user_${gameId}`);
            }
          } catch (sessionError) {
            console.error('Error restoring session:', sessionError);
            sessionStorage.removeItem(`secretSanta_user_${gameId}`);
          }
        }
      }
    } catch (err) {
      console.error('Error loading game:', err);
      setError('An unexpected error occurred. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = () => {
    if (!game || !selectedName) return;
    // Check if browser is locked to someone else
    const lockedUser = localStorage.getItem(`secretSanta_locked_${gameId}`);
    if (lockedUser && lockedUser !== selectedName) {
      setError(`This browser is locked to ${lockedUser}. Use a different browser or device to login as ${selectedName}.`);
      return;
    }
    const foundParticipant = game.participants.find(p => p.name === selectedName);
    if (foundParticipant) {
      setParticipant(foundParticipant);
      setError('');
      // Store session
      try {
        sessionStorage.setItem(`secretSanta_user_${gameId}`, JSON.stringify(foundParticipant));
        console.log('✅ Session saved for:', foundParticipant.name);
      } catch (storageError) {
        console.error('Error saving session:', storageError);
      }
    } else {
      setError('Please select your name from the list.');
    }
  };
  const handleRevealClick = () => {
    if (participant && game) {
      // Lock this browser to this participant permanently
      localStorage.setItem(`secretSanta_locked_${gameId}`, participant.name);
      setIsLocked(true);
      console.log('🔒 Browser locked to:', participant.name);
      // Mark as revealed in game data
      const savedGames = localStorage.getItem('secretSantaGames');
      if (savedGames) {
        const games: Game[] = JSON.parse(savedGames);
        const updatedGames = games.map(g => {
          if (g.id === gameId) {
            return {
              ...g,
              participants: g.participants.map(p => p.name === participant.name ? {
                ...p,
                hasRevealed: true
              } : p)
            };
          }
          return g;
        });
        localStorage.setItem('secretSantaGames', JSON.stringify(updatedGames));
      }
      navigate(`/fun/secret-santa/${gameId}/reveal`);
    }
  };
  const handleUnlock = () => {
    if (confirm('Are you sure you want to unlock this browser? This should only be done if you need to switch users on a shared device.')) {
      localStorage.removeItem(`secretSanta_locked_${gameId}`);
      sessionStorage.removeItem(`secretSanta_user_${gameId}`);
      setIsLocked(false);
      setParticipant(null);
      setSelectedName('');
      alert('Browser unlocked. You can now login as a different participant.');
    }
  };
  if (loading) {
    return <div className="container mx-auto px-4 py-16 text-center" data-id="element-495">
        <Card className="max-w-md mx-auto" data-id="element-496">
          <CardContent className="pt-6" data-id="element-497">
            <RefreshCw className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" data-id="element-498" />
            <p className="text-muted-foreground" data-id="element-499">Loading game...</p>
          </CardContent>
        </Card>
      </div>;
  }
  if (!game && error) {
    return <div className="container mx-auto px-4 py-16 text-center" data-id="element-500">
        <Card className="max-w-md mx-auto" data-id="element-501">
          <CardContent className="pt-6" data-id="element-502">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" data-id="element-503" />
            <h2 className="text-xl font-bold mb-2" data-id="element-504">Game Not Found</h2>
            <p className="text-muted-foreground mb-4" data-id="element-505">{error}</p>
            <div className="space-y-2" data-id="element-506">
              <Button onClick={loadGame} variant="outline" className="w-full" data-id="element-507">
                <RefreshCw className="w-4 h-4 mr-2" data-id="element-508" />
                Try Again
              </Button>
              <Button onClick={() => navigate('/fun/secret-santa')} variant="outline" className="w-full" data-id="element-509">
                Create New Game
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  if (!game) return null;
  return <div className="container mx-auto px-4 py-8 max-w-2xl" data-id="element-510">
      <div className="mb-8 text-center" data-id="element-511">
        <h1 className="text-4xl font-bold mb-2" data-id="element-512">{game.gameName}</h1>
        <p className="text-muted-foreground" data-id="element-513">
          Select your name to reveal your Secret Santa assignment
        </p>
        {isLocked && <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 rounded-full text-sm" data-id="element-514">
            <Lock className="w-4 h-4" data-id="element-515" />
            Browser locked to {selectedName}
          </div>}
      </div>

      {!participant ? <Card data-id="element-516">
          <CardHeader data-id="element-517">
            <CardTitle data-id="element-518">Login as...</CardTitle>
            <CardDescription data-id="element-519">
              {isLocked ? 'This browser is locked to a specific user' : 'Select your name from the list'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-520">
            <Select label="Your Name" value={selectedName} onChange={e => setSelectedName(e.target.value)} disabled={isLocked} options={[{
          value: '',
          label: 'Select your name...'
        }, ...game.participants.map(p => ({
          value: p.name,
          label: p.email ? `${p.name} (${p.email})` : p.name
        }))]} data-id="element-521" />
            {error && <p className="text-sm text-destructive" data-id="element-522">{error}</p>}
            <Button onClick={handleLogin} className="w-full" disabled={!selectedName || isLocked} data-id="element-523">
              Continue
            </Button>
            {isLocked && <Button onClick={handleUnlock} variant="outline" className="w-full text-xs" data-id="element-524">
                Unlock Browser (Admin Only)
              </Button>}
          </CardContent>
        </Card> : <Card className="border-primary/20 shadow-lg" data-id="element-525">
          <CardHeader className="text-center bg-primary/5 rounded-t-lg pb-8" data-id="element-526">
            <Gift className="w-16 h-16 mx-auto mb-4 text-primary" data-id="element-527" />
            <CardTitle className="text-2xl" data-id="element-528">
              Welcome, {participant.name}!
            </CardTitle>
            {participant.email && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-2" data-id="element-529">
                <Mail className="w-4 h-4" data-id="element-530" />
                {participant.email}
              </div>}
            <CardDescription data-id="element-531">
              You're part of the Secret Santa exchange
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6" data-id="element-532">
            <div className="bg-muted p-4 rounded-lg space-y-3" data-id="element-533">
              <h3 className="font-semibold flex items-center gap-2" data-id="element-534">
                <AlertCircle className="w-4 h-4" data-id="element-535" />
                Game Rules
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4" data-id="element-536">
                <div data-id="element-537">
                  <p className="text-muted-foreground" data-id="element-538">Budget Limit</p>
                  <p className="font-medium text-lg" data-id="element-539">
                    {game.rules.currency === 'INR' ? '₹' : game.rules.currency === 'USD' ? '$' : game.rules.currency === 'EUR' ? '€' : game.rules.currency === 'GBP' ? '£' : game.rules.currency === 'AUD' ? 'A$' : ''}
                    {game.rules.budget.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-540">
                  <p className="text-muted-foreground" data-id="element-541">Currency</p>
                  <p className="font-medium text-lg" data-id="element-542">{game.rules.currency}</p>
                </div>
              </div>
              {game.rules.allowedGifts && <div className="border-t border-border pt-3" data-id="element-543">
                  <pre className="text-sm whitespace-pre-wrap font-sans" data-id="element-544">
                    {game.rules.allowedGifts}
                  </pre>
                </div>}
            </div>

            <div className="text-center space-y-3" data-id="element-545">
              <p className="text-muted-foreground mb-4" data-id="element-546">
                Ready to find out who you're buying a gift for?
              </p>
              <Button onClick={handleRevealClick} className="w-full py-6 text-lg font-bold animate-pulse hover:animate-none" data-id="element-547">
                Reveal My Secret Santa 🎅
              </Button>
              {isLocked && <p className="text-xs text-muted-foreground" data-id="element-548">
                  <Lock className="w-3 h-3 inline mr-1" data-id="element-549" />
                  After revealing, this browser will be permanently locked to
                  your name
                </p>}
            </div>
          </CardContent>
        </Card>}
    </div>;
}